/*@jsxRuntime automatic @jsxImportSource react*/
import {Fragment as _Fragment, jsx as _jsx, jsxs as _jsxs} from "react/jsx-runtime";
import {useMDXComponents as _provideComponents} from "@mdx-js/react";
import {Docs} from 'components/layout';
import {getIntegrations} from 'api/get-integrations';
import {getExtensions} from 'api/get-extensions';
import {Warning, Tip, Info, WhitelistDetails} from 'ui';
export const lastUpdatedDate = new Date(1790121600000);
export async function getStaticProps() {
  const integrations = await getIntegrations();
  const extensions = await getExtensions();
  return {
    props: {
      ...integrations,
      extensions,
      meta: {
        title: null,
        description: null,
        lastUpdatedDate: "Wed Sep 23 2026 00:00:00 GMT+0000 (Coordinated Universal Time)",
        slug: "vistar-media",
        type: "destination",
        icon: null,
        toc: [{
          "value": "Overview",
          "depth": 0,
          "id": "overview"
        }, {
          "value": "Supported syncing",
          "depth": 0,
          "id": "supported-syncing"
        }, {
          "value": "Connect to Vistar Media",
          "depth": 0,
          "id": "connect-to-vistar-media"
        }, {
          "value": "Syncing custom audiences",
          "depth": 1,
          "id": "syncing-custom-audiences"
        }, {
          "value": "Tips and troubleshooting",
          "depth": 0,
          "id": "tips-and-troubleshooting"
        }, {
          "value": "Refresh rate",
          "depth": 1,
          "id": "refresh-rate"
        }, {
          "value": "Matched device count",
          "depth": 1,
          "id": "matched-device-count"
        }, {
          "value": "Common errors",
          "depth": 1,
          "id": "common-errors"
        }, {
          "value": "Live debugger",
          "depth": 1,
          "id": "live-debugger"
        }, {
          "value": "Live debugger",
          "depth": 1,
          "id": "live-debugger-1"
        }, {
          "value": "Sync alerts",
          "depth": 1,
          "id": "sync-alerts"
        }]
      }
    },
    revalidate: 60
  };
}
const MDXLayout = ({children, ...props}) => _jsx(Docs, Object.assign({}, props, {
  children: children
}));
function MDXContent(props = {}) {
  return MDXLayout ? _jsx(MDXLayout, Object.assign({}, props, {
    children: _jsx(_createMdxContent, {})
  })) : _createMdxContent();
  function _createMdxContent() {
    const _components = Object.assign({
      h2: "h2",
      p: "p",
      table: "table",
      thead: "thead",
      tr: "tr",
      th: "th",
      tbody: "tbody",
      td: "td",
      strong: "strong",
      a: "a",
      code: "code",
      h3: "h3",
      h4: "h4",
      ul: "ul",
      li: "li"
    }, _provideComponents(), props.components), {PylonLink, MatchBoosterDestination, CommonErrorsPreface, LiveDebugger, SyncAlerts} = _components;
    if (!CommonErrorsPreface) _missingMdxReference("CommonErrorsPreface", true);
    if (!LiveDebugger) _missingMdxReference("LiveDebugger", true);
    if (!MatchBoosterDestination) _missingMdxReference("MatchBoosterDestination", true);
    if (!PylonLink) _missingMdxReference("PylonLink", true);
    if (!SyncAlerts) _missingMdxReference("SyncAlerts", true);
    return _jsxs(_Fragment, {
      children: [_jsx(_components.h2, {
        id: "overview",
        children: "Overview"
      }), "\n", _jsx(_components.p, {
        children: "Captivate your audience with digital out-of-home (DOOH) ads that are tailored to\nthe location, weather, and time of day based on the foot patterns of your\nfirst-party audiences."
      }), "\n", _jsx(_components.h2, {
        id: "supported-syncing",
        children: "Supported syncing"
      }), "\n", _jsxs(_components.table, {
        children: [_jsx(_components.thead, {
          children: _jsxs(_components.tr, {
            children: [_jsx(_components.th, {
              children: "Sync Type"
            }), _jsx(_components.th, {
              children: "Description"
            }), _jsx(_components.th, {
              children: "Supported Sync Modes"
            })]
          })
        }), _jsx(_components.tbody, {
          children: _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.strong, {
                children: "Audiences"
              })
            }), _jsx(_components.td, {
              children: "Create and update custom audiences"
            }), _jsx(_components.td, {
              children: "All"
            })]
          })
        })]
      }), "\n", _jsxs(_components.p, {
        children: ["For more information about sync modes, refer to the ", _jsx(_components.a, {
          href: "/syncs/types-and-modes#sync-modes",
          children: "sync modes"
        }), " docs."]
      }), "\n", _jsx(_components.h2, {
        id: "connect-to-vistar-media",
        children: "Connect to Vistar Media"
      }), "\n", _jsx(_components.p, {
        children: "This integration requires prior approval from the Vistar Media team and the\nHightouch team before proceeding."
      }), "\n", _jsxs(_components.p, {
        children: ["To gain approval, please ", _jsx(PylonLink, {
          pylonText: "Hi, I need approval to connect Vistar Media.",
          linkText: "contact the Hightouch support team"
        }), " and connect us directly\nwith your Vistar Media rep who will verify approval. Once we have confirmed,\nwe will unlock the integration for you to connect."]
      }), "\n", _jsxs(_components.p, {
        children: ["To connect, input the ", _jsx(_components.code, {
          children: "Client Name"
        }), " provided by your Vistar rep."]
      }), "\n", _jsx(_components.h3, {
        id: "syncing-custom-audiences",
        children: "Syncing custom audiences"
      }), "\n", _jsx(_components.p, {
        children: "You can input the ID of the audience you plan on delivering to Vistar along with\nother properties."
      }), "\n", _jsx(_components.h4, {
        id: "user-identifiers",
        children: "User identifiers"
      }), "\n", _jsx(_components.p, {
        children: "To identify which users to add or update in an audience, select model columns\nand the corresponding Vistar fields. You can match on any of the following user\nfields:"
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsx(_components.li, {
          children: "MAID"
        }), "\n"]
      }), "\n", _jsx(MatchBoosterDestination, {
        destinationName: "Vistar Media"
      }), "\n", _jsx(_components.h4, {
        id: "delete-behavior",
        children: "Delete behavior"
      }), "\n", _jsxs(_components.p, {
        children: ["The only supported sync mode is ", _jsx(_components.code, {
          children: "all"
        }), " mode, meaning that each sync run will refresh\nthe entire audience. Users removed from your audience will automatically be dropped\nwith each refresh - only present users will be in Vistar's audience."]
      }), "\n", _jsx(_components.h2, {
        id: "tips-and-troubleshooting",
        children: "Tips and troubleshooting"
      }), "\n", _jsx(_components.h3, {
        id: "refresh-rate",
        children: "Refresh rate"
      }), "\n", _jsx(_components.p, {
        children: "Vistar Media has a max refresh rate of monthly. Though they recommend a one-and-done\nsync run for each campaign. You should not have your Vistar syncs running more than\nevery 4 weeks (we recommend a manual schedule configuration)."
      }), "\n", _jsx(_components.h3, {
        id: "matched-device-count",
        children: "Matched device count"
      }), "\n", _jsx(_components.p, {
        children: "Match rates are not available to preview in the Hightouch platform. Contact your\nVistar rep for insights into match rates and audience reach."
      }), "\n", _jsx(_components.p, {
        children: "Keep in mind that audience counts reflect the number of devices match, not\nthe number of users matched. Also keep in mind that matching occurs at the\nhousehold level, and that a single household may own multiple devices."
      }), "\n", _jsx(_components.h3, {
        id: "common-errors",
        children: "Common errors"
      }), "\n", _jsx(CommonErrorsPreface, {}), "\n", _jsx(_components.h3, {
        id: "live-debugger",
        children: "Live debugger"
      }), "\n", _jsxs(_components.p, {
        children: ["Live debugger view is not support for this integration. Please ", _jsx(PylonLink, {
          pylonText: "Hi, I'm seeing an issue with the Vistar Media destination.",
          linkText: "reach out to us"
        }), "\nif you see any issues."]
      }), "\n", _jsx(_components.h3, {
        id: "live-debugger-1",
        children: "Live debugger"
      }), "\n", _jsx(LiveDebugger, {}), "\n", _jsx(_components.h3, {
        id: "sync-alerts",
        children: "Sync alerts"
      }), "\n", _jsx(SyncAlerts, {})]
    });
  }
}
export default MDXContent;
function _missingMdxReference(id, component) {
  throw new Error("Expected " + (component ? "component" : "object") + " `" + id + "` to be defined: you likely forgot to import, pass, or provide it.");
}
